import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';

import { ItemCount } from '@pkg/components';
import { useAppSelector } from '@pkg/hooks';

import { ArtifactType } from '@app/client/common/structs/nft/artifacts';
import { getTranslationByArtifactKey } from '@app/helpers';
import { userArtifacts } from '@app/store/selectors/artifacts/artifacts';
import { usersOwnedArtifacts } from '@app/store/selectors/artifacts/artifacts-metadata';
import { firstCelestialRoseSelector } from '@app/store/selectors/artifacts/celestial-roses';
import { artifactKeyToUrlMap } from '@app/store/types/artifacts';

import { Typography } from '../../DesignSystem';

import './ArtifactsNavigation.scss';

interface YatArtifactsNavigationProps {
    className?: string;
}

export const ArtifactsNavigation = ({ className = '' }: YatArtifactsNavigationProps) => {
    const { t } = useTranslation(['artifacts']);
    const navigate = useNavigate();
    const { collectedKeys, uncollectedKeys } = useAppSelector(userArtifacts);
    const userArtifactOwnedItems = useAppSelector(usersOwnedArtifacts);

    const firstCelestialRose = useAppSelector(firstCelestialRoseSelector);
    const firstCelestialRoseId = firstCelestialRose?.id;

    // The celestial roses list only displays when there's an artifact id
    // This handler supersedes the normal NavLink and sends the user to a specific celestial rose
    // We keep the NavLink "to" for highlighting purposes
    const handleCelestialRoseClick = useCallback(
        function handleCelestialRoseClick(event) {
            event.preventDefault();
            // if they have them, the first in their list
            // if they don't have them, the first in the entire list
            navigate(`/artifacts/celestial-rose/${firstCelestialRoseId || ''}`);
        },
        [navigate, firstCelestialRoseId],
    );

    return (
        <nav className={`yat-artifacts__nav ${className}`}>
            {collectedKeys.length > 0 && (
                <Typography className="yat-artifacts__nav__subtitle" type="label" size={16}>
                    {t('my_collection')}
                </Typography>
            )}
            {collectedKeys?.map((key) => {
                const totalInCollection = userArtifactOwnedItems[key]?.length;

                return (
                    <div className="yat-artifacts__nav__item" key={key}>
                        <NavLink
                            className={({ isActive }) =>
                                'yat-artifacts__nav__link' + (isActive ? ' yat-artifacts__nav__link--active' : '')
                            }
                            to={`/artifacts/${artifactKeyToUrlMap.get(key as ArtifactType)}`}
                            onClick={key === ArtifactType.CelestialRose ? handleCelestialRoseClick : undefined}
                        >
                            {getTranslationByArtifactKey(t, key)}
                        </NavLink>
                        <ItemCount count={totalInCollection} />
                    </div>
                );
            })}

            {uncollectedKeys?.length > 0 && (
                <Typography className="yat-artifacts__nav__subtitle" type="label" size={16}>
                    {t('not_collected')}
                </Typography>
            )}

            {uncollectedKeys?.map((key) => {
                return (
                    <div className="yat-artifacts__nav__item" key={key}>
                        <NavLink
                            className={({ isActive }) =>
                                'yat-artifacts__nav__link' + (isActive ? ' yat-artifacts__nav__link--active' : '')
                            }
                            to={`/artifacts/${artifactKeyToUrlMap.get(key)}`}
                            onClick={key === ArtifactType.CelestialRose ? handleCelestialRoseClick : undefined}
                        >
                            {getTranslationByArtifactKey(t, key)}
                        </NavLink>
                    </div>
                );
            })}
        </nav>
    );
};
